type AxiosLikeError = {
  response?: {
    status?: number;
    data?: { message?: string; errorCode?: string };
  };
  request?: unknown;
  message?: string;
};

const TECHNICAL_MESSAGE =
  /^(HTTP \d{3}|Request failed with status code|Server error|Failed to fetch|Network Error|Error (saving|loading|removing|deleting|updating))/i;

const TECHNICAL_CONTENT =
  /appsettings|database|sql|exception|errorCode|asp-admin|could not reach|stripe|not configured|check if the backend is running|Unknown error/i;

const ERROR_CODE_MESSAGES: Record<string, string> = {
  VISIT_LOCKED:
    'This visit is currently being edited by another user. Please wait or contact them.',
  VISIT_NOT_FOUND: 'This visit could not be found. Please refresh the queue.',
  UNPICK_NOT_ALLOWED: 'You can only unpick visits that you picked.',
  ACTIVE_CONSULT_IN_PROGRESS:
    'You can only pick one patient at a time. Please finish your current consultation first.',
  INTERNAL_ERROR: 'Something went wrong. Please try again.',
  INVALID_USER: 'Your session has expired. Please sign in again.',
  MC_DELETE_FAILED: 'Could not delete the medical certificate. Please try again.',
  MC_CREATE_FAILED: 'Could not save the medical certificate. Please try again.',
  PATIENT_ID_VET_INVALID: 'Please enter a remark when rejecting the ID document.',
  ADMIN_ONLY: 'Management Panel is limited to LKH, LSC, DMI, TWH, Admin, and OWH.',
  DATE_RANGE_TOO_LONG: 'Please choose a date range of 366 days or less.',
  MANAGEMENT_LOAD_FAILED: 'Could not load management data. Restart ASP-Admin API and try again.',
};

function isAxiosLike(err: unknown): err is AxiosLikeError {
  return typeof err === 'object' && err !== null && 'response' in err;
}

function isSafeUserMessage(message: string): boolean {
  const trimmed = message.trim();
  if (!trimmed || trimmed.length > 280) return false;
  if (TECHNICAL_MESSAGE.test(trimmed)) return false;
  if (TECHNICAL_CONTENT.test(trimmed)) return false;
  if (/\(\d{3}\)/.test(trimmed)) return false;
  return true;
}

function messageFromResponse(data: { message?: string; errorCode?: string } | undefined): string | null {
  const code = data?.errorCode?.trim();
  const message = data?.message?.trim();

  // Staff refund/cancel messages are written for the dialog â€” show them even if they mention Stripe.
  if ((code === 'REFUND_INVALID' || code === 'CANCEL_INVALID') && message && message.length <= 280) {
    return message;
  }

  if (code && ERROR_CODE_MESSAGES[code]) {
    return ERROR_CODE_MESSAGES[code];
  }

  if (message && isSafeUserMessage(message)) {
    return message;
  }

  return null;
}

/** Read ASP-Admin API errors and return human-readable text safe for the UI. */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosLike(err)) {
    const status = err.response?.status;
    const fromBody = messageFromResponse(err.response?.data);
    if (fromBody) return fromBody;

    if (status === 403) {
      return 'You do not have access to this.';
    }

    if (status === 409 && err.response?.data?.errorCode === 'VISIT_LOCKED') {
      return ERROR_CODE_MESSAGES.VISIT_LOCKED;
    }

    if (status != null && status >= 500) {
      return fallback;
    }

    if (err.request && !err.response) {
      return 'Unable to reach the server. Check your connection and try again.';
    }
  }

  if (err instanceof Error) {
    const msg = err.message.trim();
    if (msg && isSafeUserMessage(msg)) {
      return msg;
    }
  }

  return fallback;
}

/** Shorthand when the fallback is the only user-facing message you want for failures. */
export function getSafeErrorMessage(err: unknown, fallback: string): string {
  return getApiErrorMessage(err, fallback);
}
